import { grpc } from '@improbable-eng/grpc-web';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb';
import { ObjectRef } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/common/common_pb';
import { PageSpec } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/portal_pb';
import {
  AddAPIKeyRequest,
  AddAPIKeyResponse,
  APIProductDetails,
  APIProductListResponse,
  APIProductPlanListSummary,
  APIProductVersionDetails,
  APIProductVersionDetailsRequest,
  ChangePasswordRequest,
  DynamicPage,
  EnvironmentPlans,
  GetCustomStylingResponse,
  GetPortalSummaryResponse,
  GRPCInvokeRequest,
  GRPCInvokeResponse,
  LoginRequest,
  LoginResponse
} from 'proto/web_app_grpc_pb';
import { ServiceError, WebAppApiClient } from 'proto/web_app_grpc_pb_service';

export const host = `${process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:1234'}`;

export const webAppClient = new WebAppApiClient(host, {
  transport: grpc.CrossBrowserHttpTransport({ withCredentials: true }),
  debug: process.env.NODE_ENV === 'development'
});

export const webAppAPI = {
  login,
  logout,
  getCurrentUser,
  getPortalSummary,
  changePassword,
  getCustomStyles,
  getStaticPages,
  getDynamicPages,
  listAPIProducts,
  getAPIProductDetails,
  getAPIProductVersionDetails,
  listAPIProductPlanDetails,
  listAPIProductEnvironmentPlans,
  addAPIKey,
  deleteAPIKey,
  invokeGrpcMethod,
  getPageExtensionsMap
};

function login(loginRequest: LoginRequest.AsObject): Promise<LoginResponse.AsObject> {
  return new Promise((resolve, reject) => {
    let request = new LoginRequest();

    let basicAuth = new LoginRequest.BasicAuth();
    if (loginRequest.basicAuth) {
      basicAuth.setPassword(loginRequest.basicAuth?.password);
      basicAuth.setUsername(loginRequest.basicAuth?.username);
    }

    request.setBasicAuth(basicAuth);

    webAppClient.login(request, (error, data) => {
      if (error !== null) {
        logError(error);

        resolve(undefined!!);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function changePassword(changePasswordRequest: ChangePasswordRequest.AsObject): Promise<Empty.AsObject> {
  return new Promise((resolve, reject) => {
    let request = new ChangePasswordRequest();

    request.setCurrentPassword(changePasswordRequest.currentPassword);
    request.setNewPassword(changePasswordRequest.newPassword);

    webAppClient.changePassword(request, (error, data) => {
      if (error !== null) {
        logError(error);

        resolve(undefined!);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function getCurrentUser(): Promise<string> {
  return new Promise((resolve, reject) => {
    webAppClient.getCurrentUser(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        // An error here means we don't know who the current user is, or if there is one,
        // the caller should handle an error accordingly
        reject(error);
      } else if (!data?.toObject().username && data?.toObject().publicAccessUser) {
        reject("publicAccessUser"); // publicAccessUser is a placeholder, not a real user
      } else {
        resolve(data?.toObject().username!);
      }
    });
  });
}

function logout(): Promise<Empty.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.logout(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        reject(error);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function getPortalSummary(): Promise<GetPortalSummaryResponse.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.getPortalSummary(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        reject(error);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function getCustomStyles(): Promise<GetCustomStylingResponse.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.getCustomStyling(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function listAPIProducts(): Promise<APIProductListResponse.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.listAPIProducts(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function getAPIProductDetails(ref: ObjectRef.AsObject): Promise<APIProductDetails.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.getAPIProductDetails(toObjectRef(ref), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data?.toObject()!);
      }
    });
  });
}

function listAPIProductPlanDetails(): Promise<APIProductPlanListSummary.AsObject[]> {
  return new Promise((resolve, reject) => {
    webAppClient.listAPIProductPlans(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data?.toObject().apiProductsList!);
      }
    });
  });
}

function getAPIProductVersionDetails(
  req: APIProductVersionDetailsRequest.AsObject
): Promise<APIProductVersionDetails.AsObject> {
  let request = new APIProductVersionDetailsRequest();
  let { apiProductRef, environmentRef, version } = req;
  request.setApiProductRef(toObjectRef(apiProductRef));
  request.setEnvironmentRef(toObjectRef(environmentRef));
  request.setVersion(version);

  return new Promise((resolve, reject) => {
    webAppClient.getAPIProductVersionDetails(request, (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data!.toObject());
      }
    });
  });
}

function listAPIProductEnvironmentPlans(ref: ObjectRef.AsObject): Promise<EnvironmentPlans.AsObject[]> {
  return new Promise((resolve, reject) => {
    webAppClient.listAPIProductPlanDetails(toObjectRef(ref), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data!.toObject().environmentsList);
      }
    });
  });
}

function getStaticPages(): Promise<PageSpec.AsObject[]> {
  return new Promise((resolve, reject) => {
    webAppClient.getStaticPages(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        reject(error);
      } else {
        resolve(data?.toObject().staticPagesList!);
      }
    });
  });
}

function getDynamicPages(): Promise<DynamicPage.AsObject[]> {
  return new Promise((resolve, reject) => {
    webAppClient.getDynamicPages(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        reject(error);
      } else {
        resolve(data?.toObject().dynamicPagesList!);
      }
    });
  });
}

function getPageExtensionsMap(): Promise<[string, string][]> {
  return new Promise((resolve, reject) => {
    webAppClient.getPageExtensions(new Empty(), (error, data) => {
      if (error !== null) {
        logError(error);

        reject(error);
      } else {
        resolve(data?.toObject().pageExtensionsMap!);
      }
    });
  });
}

function addAPIKey(req: AddAPIKeyRequest.AsObject): Promise<AddAPIKeyResponse.AsObject> {
  return new Promise((resolve, reject) => {
    let request = new AddAPIKeyRequest();
    let { apiProductRef, environmentRef, usagePlanName, customStringDataMap } = req;
    request.setApiProductRef(toObjectRef(apiProductRef));
    request.setEnvironmentRef(toObjectRef(environmentRef));
    request.setUsagePlanName(usagePlanName);
    // Set the metadata k/v
    if (customStringDataMap !== undefined) {
      customStringDataMap.forEach(([key, value]) => request.getCustomStringDataMap().set(key, value));
    }

    webAppClient.addAPIKey(request, (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data!.toObject());
      }
    });
  });
}

function deleteAPIKey(req: ObjectRef.AsObject): Promise<Empty.AsObject> {
  return new Promise((resolve, reject) => {
    webAppClient.deleteAPIKey(toObjectRef(req), (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data!.toObject());
      }
    });
  });
}

function invokeGrpcMethod(req: GRPCInvokeRequest.AsObject): Promise<GRPCInvokeResponse.AsObject> {
  let request = new GRPCInvokeRequest();
  let {
    apiKey,
    argumentJson,
    apiProductRef,
    environmentRef,
    methodName,
    serviceDomain,
    serviceName,
    oauthAccessToken,
    version
  } = req;

  let apRef = objectRefFromObj(apiProductRef);
  let envRef = objectRefFromObj(environmentRef);

  request.setApiProductRef(apRef);
  request.setEnvironmentRef(envRef);
  request.setVersion(version);
  request.setServiceName(serviceName);
  request.setServiceDomain(serviceDomain);
  request.setMethodName(methodName);
  request.setArgumentJson(argumentJson);

  if (!!oauthAccessToken) {
    request.setOauthAccessToken(oauthAccessToken);
  } else {
    request.setApiKey(apiKey);
  }

  return new Promise((resolve, reject) => {
    webAppClient.invokeGRPCMethod(request, (error, data) => {
      if (error !== null) {
        logError(error);
        reject(error);
      } else {
        resolve(data!.toObject());
      }
    });
  });
}

export function logError(error: ServiceError) {
  console.error('Error:', error.message);
  console.error('Code:', error.code);
  console.error('Metadata:', error.metadata);
}

export function toObjectRef(ref: ObjectRef.AsObject | undefined): ObjectRef {
  if (ref === undefined!) {
    return new ObjectRef();
  }

  let { name, namespace } = ref!;
  let request = new ObjectRef();
  request.setName(name);
  request.setNamespace(namespace);
  return request;
}

export function objectRefFromObj(obj: ObjectRef.AsObject | undefined) {
  let ref = new ObjectRef();
  if (!!obj) {
    ref.setName(obj.name);
    ref.setNamespace(obj.namespace);
  }
  return ref;
}
